import { useState, useMemo, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { ArrowLeft, Upload, User, Search, Bell, ChevronDown, Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import { TimePicker } from "antd";

import "../styles/applyLeavePage.css";
import MainLayout from "./MainLayout";
import GlobalBreadcrumb from "./GlobalBreadcrumb";
import { useAuth } from "../context/AuthContext";

import toast from 'react-hot-toast';
import {
  createLeaveRequest,
  getLeaveTypes,
  getLeaveBalances,
  getLeavesByEmployee,
  getEmployeeById,
  uploadLeaveAttachments,
  computeDynamicBalances,
  getLeavePolicies,
  isWFH,
} from "../services/leaveApiService";   // ← centralised service

// ─── helpers ─────────────────────────────────────────────────
function minutesDiff(from, to) {
  if (!from || !to) return 0;
  const [fh, fm] = from.split(":").map(Number);
  const [th, tm] = to.split(":").map(Number);
  return th * 60 + tm - (fh * 60 + fm);
}

function formatDuration(minutes) {
  const h = Math.floor(Math.abs(minutes) / 60);
  const m = Math.abs(minutes) % 60;
  return `${h > 0 ? h + "h " : ""}${m > 0 ? m + "m" : ""}`.trim() || "0m";
}

// ─── sub-components ──────────────────────────────────────────
function TimeDurationBadge({ fromTime, toTime, unitLabel }) {
  const diff = minutesDiff(fromTime, toTime);
  if (!fromTime || !toTime) return null;
  if (diff <= 0)
    return <p className="al-time-error">End time must be after start time</p>;
  const hrs = (diff / 60).toFixed(2);
  const days = (diff / (8 * 60)).toFixed(2);
  return (
    <div className="al-time-summary">
      <span className="al-time-summary-icon">⏱</span>
      <span>Duration: <strong>{formatDuration(diff)}</strong></span>
      <span className="al-time-summary-pill">
        = {unitLabel === "hrs" ? `${hrs} hrs` : `${days} day`}
      </span>
    </div>
  );
}

function PreviewRow({ label, value, unit, highlight, danger }) {
  return (
    <div className="al-preview-row">
      <span className="al-preview-label">{label}</span>
      <span className={`al-preview-value${highlight ? " highlight" : ""}${danger ? " danger" : ""}`}>
        {value} {unit}
      </span>
    </div>
  );
}

// ─── Toast helper (Removed - using react-toastify) ─────────────────────────

// ─────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────
export default function ApplyLeavePage() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen((p) => !p);

  // Auth
  const { user, token } = useAuth();

  const [managerName, setManagerName] = useState("Your Manager");

  // user.empId = "SFW-IN-0045" (string from login response)
  const employeeId = user?.employee_id ?? user?.employeeId ?? user?.empId ?? user?.id ?? null;

  // DEBUG — remove after confirming
  console.log("[ApplyLeave] user:", user);
  console.log("[ApplyLeave] employeeId:", employeeId);
  console.log("[ApplyLeave] token:", token ? "present" : "missing");
  console.log("[ApplyLeave] manager_name:", user?.manager_name);
  console.log("[ApplyLeave] manager_id:", user?.manager_id);
  console.log("[ApplyLeave] user FULL:", JSON.stringify(user, null, 2));

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const lt = params.get("leaveType");
    if (lt) {
      setSelectedLeaveType(lt);
    }
  }, [location.search]);

  // Remote data
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [leaveHistory, setLeaveHistory] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [fetchError, setFetchError] = useState("");

  // Form state
  const [search, setSearch] = useState("");
  const [selectedLeaveType, setSelectedLeaveType] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isHalfDay, setIsHalfDay] = useState(false);
  const [halfDayType, setHalfDayType] = useState("first");
  const [employeeShift, setEmployeeShift] = useState("");
  const [reason, setReason] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [attachError, setAttachError] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [previewImage, setPreviewImage] = useState(null);

  // Submit state
  const [submitting, setSubmitting] = useState(false);

  // // ── Fetch leave types + balances on mount ──────────────────
  // useEffect(() => {
  //   // If employeeId missing — user object field name mismatch
  //   if (!employeeId) {
  //     setLoadingData(false);
  //     setFetchError("Could not resolve employee ID. Check user object in console.");
  //     return;
  //   }



  //   const load = async () => {
  //     setLoadingData(true);
  //     setFetchError("");
  //     try {
  //       console.log("[ApplyLeave] Calling getLeaveTypes...");
  //       const types = await getLeaveTypes(token);
  //       console.log("[ApplyLeave] leaveTypes response:", types);

  //       console.log("[ApplyLeave] Calling getLeaveBalances for employeeId:", employeeId);
  //       const balances = await getLeaveBalances(employeeId, token);
  //       console.log("[ApplyLeave] balances response:", balances);

  //       setLeaveTypes(types.filter((lt) => lt.is_active && !lt.is_deleted));
  //       setLeaveBalances(balances);
  //     } catch (err) {
  //       console.error("[ApplyLeave] Load error:", err);
  //       setFetchError(err.message || "Failed to load leave data");
  //     } finally {
  //       setLoadingData(false);
  //     }
  //   };

  //   load();
  // }, [employeeId, token]);
  // ── Fetch manager name ──────────────────────────────────
  useEffect(() => {
    if (!employeeId || !token) return;

    getEmployeeById(employeeId, token)
      .then((emp) => {
        console.log("[ApplyLeave] employee record:", emp);
        setManagerName(emp?.manager_name ?? "Your Manager");
        setEmployeeShift(emp?.shift_name || emp?.shiftTiming || emp?.shift_timing || "");
      })
      .catch((err) => console.error("[ApplyLeave] employee fetch error:", err));
  }, [employeeId, token]);

  // ── Fetch leave types + balances ────────────────────────
  useEffect(() => {
    if (!employeeId) {
      setLoadingData(false);
      setFetchError("Could not resolve employee ID. Check user object in console.");
      return;
    }

    const load = async () => {
      setLoadingData(true);
      setFetchError("");
      try {
        const types = await getLeaveTypes(token);
        const balancesRaw = await getLeaveBalances(employeeId, token);
        const history = await getLeavesByEmployee(employeeId, token);
        const policies = await getLeavePolicies(token);

        setLeaveHistory(history);
        setLeaveTypes(types.filter((lt) => lt.is_active && !lt.is_deleted && !isWFH(lt)));
        setLeaveBalances(computeDynamicBalances(balancesRaw, history, types, policies));
      } catch (err) {
        setFetchError(err.message || "Failed to load leave data");
      } finally {
        setLoadingData(false);
      }
    };

    load();
  }, [employeeId, token]);

  // ── Derived values ─────────────────────────────────────────
  const minDateStr = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 2);
    return d.toISOString().split("T")[0];
  }, []);

  const selectedType = useMemo(
    () => leaveTypes.find((l) => String(l.id) === selectedLeaveType),
    [leaveTypes, selectedLeaveType]
  );

  const balance = useMemo(
    () => leaveBalances.find((b) => String(b.leave_type_id) === selectedLeaveType),
    [leaveBalances, selectedLeaveType]
  );

  const isHourBased = selectedType?.unit_type === "HOUR";
  const isSingleDay = startDate && endDate && startDate === endDate;
  const available = useMemo(() => {
    return parseFloat(balance?.available ?? 0);
  }, [balance]);

  const unit = isHourBased ? "hrs" : "days";
  const timeDiffMinutes = minutesDiff(startTime, endTime);
  const timeIsValid = timeDiffMinutes > 0;

  const requestedUnits = useMemo(() => {
    if (isHourBased) return timeIsValid ? parseFloat((timeDiffMinutes / 60).toFixed(2)) : 0;
    if (!startDate || !endDate) return 0;
    const diff =
      Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)) + 1;
    if (isSingleDay && isHalfDay) {
      return timeIsValid ? parseFloat((timeDiffMinutes / (8 * 60)).toFixed(2)) : 0.5;
    }
    return diff;
  }, [startDate, endDate, isHalfDay, isHourBased, timeDiffMinutes, timeIsValid, isSingleDay]);

  const payable = Math.min(requestedUnits, available);
  const lop = Math.max(0, requestedUnits - available);
  const afterBalance = Math.max(0, available - requestedUnits);

  const canSubmit = !submitting; // Button is always clickable to show validation toasts

  // ── Helpers ────────────────────────────────────────────────
  const resetTimes = () => { setStartTime(""); setEndTime(""); };

  const showToast = (type, message) => {
    if (type === "error") toast.error(message);
    else if (type === "success") toast.success(message);
    else toast(message);
  };

  // ── File Management ──
  const processFiles = (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setAttachError("");

    const files = Array.from(fileList);
    const MAX_SIZE = 2 * 1024 * 1024; // 2 MB
    const ALLOWED = ["application/pdf", "image/jpeg", "image/png"];
    const fileErrors = [];
    const newFiles = [];

    for (const f of files) {
      if (!ALLOWED.includes(f.type) && f.type !== "image/jpg") {
        fileErrors.push(`"${f.name}": format not supported`);
      } else if (f.size > MAX_SIZE) {
        fileErrors.push(`"${f.name}": max 2 MB`);
      } else {
        if (f.type.startsWith("image/")) {
          f.previewUrl = URL.createObjectURL(f);
        }
        newFiles.push(f);
      }
    }

    if (fileErrors.length) {
      setAttachError(fileErrors.join(" | "));
    }

    if (attachments.length + newFiles.length > 5) {
      setAttachError("Max 5 attachments allowed");
      return;
    }

    if (newFiles.length > 0) {
      setAttachments((prev) => [...prev, ...newFiles]);
    }
  };

  const handleFileSelect = (e) => {
    processFiles(e.target.files);
    e.target.value = "";
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFiles(e.dataTransfer.files);
    }
  };

  const removeAttachment = (idx) =>
    setAttachments((prev) => prev.filter((_, i) => i !== idx));


  // ── Determine request_type for backend ────────────────────
  const getRequestType = () => {
    if (isHourBased) return "PERMISSION";
    if (isSingleDay && isHalfDay) return "HALF_DAY";
    return "FULL_DAY";
  };

  // ── Submit ─────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!selectedLeaveType) return showToast("error", "Please select a leave type.");
    if (!startDate) return showToast("error", "Please select a start date.");
    if (!isHourBased && !endDate) return showToast("error", "Please select an end date.");
    if (!reason.trim()) return showToast("error", "Please provide a reason.");

    if (isHourBased) {
      if (!startTime || !endTime) return showToast("error", "Please select start and end time.");
      if (!timeIsValid) return showToast("error", "Please select a valid time range.");
      if (requestedUnits < 0.5 || requestedUnits > 2.0) {
        return showToast("error", "Only can able to submit 30 minutes max 2 hours");
      }
      if (requestedUnits > available) {
        return showToast("error", "you reach to 2 hours limit this month");
      }
    } else {
      if (isHalfDay && isSingleDay && !timeIsValid) return showToast("error", "Please select a valid time range for half day.");
      if (requestedUnits <= 0) return showToast("error", "Requested units must be greater than 0.");

      const leaveName = (selectedType?.name || selectedType?.leave_type_name || "").toLowerCase();
      const selDate = new Date(startDate);
      const selMonth = selDate.getMonth();
      const selYear = selDate.getFullYear();

      // Date helper for "DD-MM-YYYY" or ISO formats
      const getDateInfo = (dStr) => {
        if (!dStr) return { m: -1, y: -1 };
        let d = new Date(dStr);
        if (isNaN(d.getTime()) && dStr.includes("-")) {
          const p = dStr.split("-");
          if (p[2]?.length === 4) d = new Date(p[2], parseInt(p[1])-1, p[0]);
        }
        return { m: d.getMonth(), y: d.getFullYear() };
      };

      // Casual Leave Validation
      if (leaveName.includes("casual")) {
        const casualUsedThisMonth = leaveHistory
          .filter(h => {
            const matchesType = String(h.leave_type_id || h.leaveTypeId) === String(selectedType.id) || (h.leave_type || h.leaveType || "").toLowerCase().includes("casual");
            const matchesStatus = (h.status || "").toUpperCase() === 'APPROVED' || (h.status || "").toUpperCase() === 'PENDING';
            const info = getDateInfo(h.start_date || h.startDate);
            return matchesType && matchesStatus && info.m === selMonth && info.y === selYear;
          })
          .reduce((sum, h) => sum + parseFloat(h.total_units || h.totalUnits || h.duration || 1), 0);

        if (casualUsedThisMonth + requestedUnits > 1.0) {
          return showToast("error", "your reached casual leave this month cant able to apply leave");
        }

        const casualUsedThisYear = leaveHistory
          .filter(h => {
            const matchesType = String(h.leave_type_id || h.leaveTypeId) === String(selectedType.id) || (h.leave_type || h.leaveType || "").toLowerCase().includes("casual");
            const matchesStatus = (h.status || "").toUpperCase() === 'APPROVED' || (h.status || "").toUpperCase() === 'PENDING';
            const info = getDateInfo(h.start_date || h.startDate);
            return matchesType && matchesStatus && info.y === selYear;
          })
          .reduce((sum, h) => sum + parseFloat(h.total_units || h.totalUnits || h.duration || 1), 0);

        if (casualUsedThisYear + requestedUnits > 12.0) {
          return showToast("error", "your reached casual leave this year cant able to apply leave");
        }
      }
      // Sick Leave Validation
      else if (leaveName.includes("sick")) {
        const sickUsedThisYear = leaveHistory
          .filter(h => {
            const matchesType = String(h.leave_type_id || h.leaveTypeId) === String(selectedType.id) || (h.leave_type || h.leaveType || "").toLowerCase().includes("sick");
            const matchesStatus = (h.status || "").toUpperCase() === 'APPROVED' || (h.status || "").toUpperCase() === 'PENDING';
            const info = getDateInfo(h.start_date || h.startDate);
            return matchesType && matchesStatus && info.y === selYear;
          })
          .reduce((sum, h) => sum + parseFloat(h.total_units || h.totalUnits || h.duration || 1), 0);

        if (sickUsedThisYear + requestedUnits > 6.0) {
          return showToast("error", "your reached sick leave this year cant able to apply leave");
        }
      }
    }

    setSubmitting(true);

    try {
      // Build multipart only if file upload needed; else JSON
      const payload = {
        employee_id: employeeId,
        leave_type_id: selectedLeaveType,
        start_date: startDate,
        end_date: endDate || startDate,
        total_units: requestedUnits,
        reason,
        request_type: getRequestType(),
        start_time: startTime || null,
        end_time: endTime || null,
      };

      const result = await createLeaveRequest(payload, token);
      const leaveId = result?.id || result?.data?.id;

      // Upload attachments if any
      if (attachments.length > 0 && leaveId) {
        try {
          await uploadLeaveAttachments(
            leaveId,
            attachments,
            user.name || user.empId || "employee",
            token
          );
        } catch (upErr) {
          console.warn("Attachment upload failed:", upErr.message);
        }
      }

      showToast("success", "✓ Leave request submitted successfully!");

      setTimeout(() => {
        navigate("/dashboard");
      }, 1200);

      // Reset form
      setSelectedLeaveType("");
      setStartDate(""); setEndDate("");
      setIsHalfDay(false); resetTimes();
      setReason(""); setAttachments([]); setAttachError("");

      // Refresh balances
      const freshBalancesRaw = await getLeaveBalances(employeeId, token);
      const freshHistory = await getLeavesByEmployee(employeeId, token);
      const freshPolicies = await getLeavePolicies(token);
      setLeaveHistory(freshHistory);
      setLeaveBalances(computeDynamicBalances(freshBalancesRaw, freshHistory, leaveTypes, freshPolicies));

    } catch (err) {
      showToast("error", err.message || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────
  return (
    <>
      <MainLayout sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar} title="Apply Leave" subtitle="Submit a new leave request">
        <div className="al-wrap" style={{ padding: '0' }}>

          {/* ── Content ── */}
          <main className="al-content">

            {/* Toast Container */}
            {/* Data fetch error */}
            {fetchError && (
              <div className="al-error-banner">
                <AlertCircle size={16} /> {fetchError}
              </div>
            )}

            {/* Loading skeleton */}
            {loadingData ? (
              <div className="al-loading">
                <Loader2 size={24} className="al-spin" />
                <span>Loading leave data…</span>
              </div>
            ) : (

              <div className="al-layout">

                {/* ── Form Card ── */}
                <div className="al-form-card">
                  <h2 className="al-form-title">Leave Request Form</h2>

                  {/* Leave Type */}
                  <div className="al-field">
                    <label className="al-label">Leave Type</label>
                    <div className="al-select-wrap">
                      <select
                        className="al-select"
                        value={selectedLeaveType}
                        onChange={(e) => {
                          setSelectedLeaveType(e.target.value);
                          setStartDate(""); setEndDate("");
                          setIsHalfDay(false); resetTimes();
                        }}
                      >
                        <option value="">Select leave type</option>
                        {leaveTypes.map((lt) => (
                          <option key={lt.id} value={String(lt.id)}>
                            {lt.name} ({lt.unit_type === "HOUR" ? "Hours" : "Days"})
                          </option>
                        ))}
                      </select>
                      <ChevronDown size={16} className="al-select-arrow" />
                    </div>

                    {/* Balance badge */}
                    {balance && (
                      <span className="al-balance-badge">
                        Balance: <strong>{balance.available} {unit}</strong>
                      </span>
                    )}
                  </div>

                  {/* ── Day-based dates ── */}
                  {!isHourBased && (
                    <div className="al-date-row">
                      <div className="al-field">
                        <label className="al-label">Start Date</label>
                        <input
                          type="date"
                          className="al-input"
                          value={startDate}
                          min={minDateStr}
                          onClick={(e) => e.target.showPicker()}
                          onChange={(e) => { setStartDate(e.target.value); setIsHalfDay(false); resetTimes(); }}
                        />
                      </div>
                      <div className="al-field">
                        <label className="al-label">End Date</label>
                        <input
                          type="date"
                          className="al-input"
                          value={endDate}
                          min={startDate}
                          onClick={(e) => e.target.showPicker()}
                          onChange={(e) => { setEndDate(e.target.value); setIsHalfDay(false); resetTimes(); }}
                        />
                      </div>
                    </div>
                  )}

                  {/* ── Half Day toggle ── */}
                  {!isHourBased && isSingleDay && (
                    <div className="al-halfday-container" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', fontWeight: 600, background: '#e0f2fe', color: '#0369a1', padding: '6px 14px', borderRadius: '6px', border: '1.5px solid #7dd3fc', transition: 'all 0.15s', userSelect: 'none' }}>
                          <input
                            type="checkbox"
                            name="halfDayType"
                            checked={isHalfDay && halfDayType === "first"}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setIsHalfDay(true);
                                setHalfDayType("first");
                                setStartTime("09:30");
                                setEndTime("13:30");
                              } else {
                                setIsHalfDay(false);
                                setHalfDayType("");
                                resetTimes();
                              }
                            }}
                            style={{ margin: 0, cursor: 'pointer', accentColor: '#0369a1' }}
                          />
                          First Half (09:30 AM - 01:30 PM)
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', fontWeight: 600, background: '#fef3c7', color: '#b45309', padding: '6px 14px', borderRadius: '6px', border: '1.5px solid #fcd34d', transition: 'all 0.15s', userSelect: 'none' }}>
                          <input
                            type="checkbox"
                            name="halfDayType"
                            checked={isHalfDay && halfDayType === "second"}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setIsHalfDay(true);
                                setHalfDayType("second");
                                setStartTime("14:30");
                                setEndTime("18:30");
                              } else {
                                setIsHalfDay(false);
                                setHalfDayType("");
                                resetTimes();
                              }
                            }}
                            style={{ margin: 0, cursor: 'pointer', accentColor: '#b45309' }}
                          />
                          Second Half (02:30 PM - 06:30 PM)
                        </label>
                      </div>
                      {isHalfDay && (
                        <div>
                          <TimeDurationBadge fromTime={startTime} toTime={endTime} unitLabel="day" />
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── Permission: date + time ── */}
                  {isHourBased && (
                    <>
                      <div className="al-field">
                        <label className="al-label">Date</label>
                        <input
                          type="date"
                          className="al-input"
                          value={startDate}
                          min={minDateStr}
                          onClick={(e) => e.target.showPicker()}
                          onChange={(e) => { setStartDate(e.target.value); setEndDate(e.target.value); resetTimes(); }}
                        />
                      </div>

                      <div className="al-time-block al-time-block--permission">
                        <p className="al-time-block-label">
                          <span className="al-time-block-dot" /> Permission Time Range
                        </p>
                        <div className="al-time-row">
                          <div className="al-field">
                            <label className="al-label">From Time</label>
                            <TimePicker
                              use12Hours
                              format="h:mm a"
                              value={startTime ? dayjs(`2000-01-01T${startTime}`) : null}
                              onChange={(time) => setStartTime(time ? time.format("HH:mm") : "")}
                              className="al-input al-time-input"
                            />
                          </div>
                          <div className="al-time-arrow">→</div>
                          <div className="al-field">
                            <label className="al-label">To Time</label>
                            <TimePicker
                              use12Hours
                              format="h:mm a"
                              value={endTime ? dayjs(`2000-01-01T${endTime}`) : null}
                              onChange={(time) => setEndTime(time ? time.format("HH:mm") : "")}
                              className="al-input al-time-input"
                            />
                          </div>
                        </div>
                        <TimeDurationBadge fromTime={startTime} toTime={endTime} unitLabel="hrs" />
                      </div>
                    </>
                  )}

                  {/* Reason */}
                  <div className="al-field">
                    <label className="al-label">Reason</label>
                    <textarea
                      className="al-textarea"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Provide a reason for your leave request..."
                      rows={4}
                    />
                  </div>

                  {/* Document upload (Always visible) */}
                  <div className="al-field">
                    <label className="al-label">
                      Attachments <span style={{ color: '#888', fontWeight: 400 }}>(PDF or JPEG, max 2 MB each)</span>
                    </label>
                    <label className={`al-upload-box ${dragActive ? 'drag-active' : ''}`}
                      onDragEnter={handleDrag}
                      onDragLeave={handleDrag}
                      onDragOver={handleDrag}
                      onDrop={handleDrop}
                    >
                      <Upload size={18} className="al-upload-icon" />
                      <span>{dragActive ? "Drop files here" : (attachments.length > 0 ? `${attachments.length} files selected` : "Click to browse or drag files here")}</span>
                      <input
                        type="file"
                        className="al-file-input"
                        multiple
                        accept="application/pdf,image/jpeg,image/png"
                        onChange={handleFileSelect}
                      />
                    </label>

                    {attachError && <small className="al-time-error" style={{ marginTop: '2px' }}>{attachError}</small>}

                    {attachments.length > 0 && (
                      <div className="al-attach-section">
                        <p className="al-attach-header">Attachment Previews</p>
                        <div className="al-attach-grid">
                          {attachments.map((f, i) => (
                            <div key={i} className="al-attach-card" onClick={() => f.previewUrl && setPreviewImage(f.previewUrl)}>
                              <div className="al-attach-preview">
                                {f.type === 'application/pdf' ? (
                                  <div className="al-pdf-icon">PDF</div>
                                ) : (
                                  <img src={f.previewUrl} alt={f.name} />
                                )}
                              </div>
                              <div className="al-attach-info">
                                <span className="al-attach-name" title={f.name}>{f.name}</span>
                                <span className="al-attach-size">({(f.size / 1024).toFixed(0)} KB)</span>
                                <div className="al-attach-timestamp">
                                  <span className="al-attach-date">{dayjs().format("DD-MM-YYYY hh:mm A")}</span>
                                </div>
                              </div>
                              <button
                                type="button"
                                className="al-attach-remove"
                                onClick={(e) => { e.stopPropagation(); removeAttachment(i); }}
                                title="Remove"
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Manager */}
                  <div className="al-manager-box">
                    <div className="al-manager-avatar"><User size={16} /></div>
                    <div>
                      <p className="al-manager-label">Request will be sent to</p>
                      <p className="al-manager-name">
                        {managerName}
                      </p>

                    </div>
                  </div>

                  {/* Actions */}
                  <div className="al-form-actions">
                    <button
                      className={`al-submit-btn${!canSubmit ? " disabled" : ""}`}
                      onClick={handleSubmit}
                    >
                      {submitting ? (
                        <><Loader2 size={14} className="al-spin" /> Submitting…</>
                      ) : (
                        "Submit Request"
                      )}
                    </button>
                    <Link to="/dashboard" className="al-cancel-btn">Cancel</Link>
                  </div>
                </div>

                {/* ── Preview Card ── */}
                <div className="al-preview-card">
                  <h3 className="al-preview-title">Leave Preview</h3>
                  <div className="al-preview-rows">
                    <PreviewRow label="Available Balance" value={available} unit={unit} />
                    <PreviewRow label="Requested" value={requestedUnits} unit={unit} />
                    <PreviewRow label="Payable Units" value={payable} unit={unit} />
                    {lop > 0 && <PreviewRow label="LOP" value={lop} unit={unit} danger />}
                    <div className="al-preview-row al-preview-total">
                      <span className="al-preview-label">Balance After</span>
                      <span className="al-preview-value highlight-blue">
                        {afterBalance} {unit}
                      </span>
                    </div>
                  </div>

                  {(isHourBased || isHalfDay) && startTime && endTime && timeIsValid && (
                    <div className="al-preview-time-box">
                      <span className="al-preview-time-label">Time</span>
                      <span className="al-preview-time-val">{startTime} → {endTime}</span>
                      <span className="al-preview-time-dur">{formatDuration(timeDiffMinutes)}</span>
                    </div>
                  )}

                  {lop > 0 && !isHourBased && (
                    <div className="al-lop-warning">
                      Exceeds balance. {lop} {unit} will be LOP.
                    </div>
                  )}

                  {lop > 0 && isHourBased && (
                    <div className="al-lop-warning" style={{ color: '#EF4444', backgroundColor: '#FEF2F2', borderColor: '#FCA5A5' }}>
                      Monthly limit exceeded. Request will be blocked.
                    </div>
                  )}

                  {/* All leave-type balances overview */}
                  {leaveBalances.length > 0 && (
                    <div className="al-balance-overview">
                      <p className="al-balance-overview-title">Your Leave Balances</p>
                      {leaveBalances.map((b) => {
                        const lt = leaveTypes.find((l) => String(l.id) === String(b.leave_type_id));
                        return (
                          <div key={b.leave_type_id} className="al-balance-overview-row">
                            <span>{b.leave_type_name || lt?.name || b.leave_type_id}</span>
                            <strong>{b.available} {lt?.unit_type === "HOUR" || b.unit_type === "HOUR" ? "hrs" : "days"}</strong>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

              </div>
            )}
          </main>
        </div>
      </MainLayout>
      {/* Preview Modal */}
      {previewImage && (
        <div className="al-image-preview-modal" onClick={() => setPreviewImage(null)}>
          <div className="al-preview-modal-content" onClick={e => e.stopPropagation()}>
            <button className="al-preview-close" onClick={() => setPreviewImage(null)}>✕</button>
            <img src={previewImage} alt="Full preview" />
          </div>
        </div>
      )}
    </>
  );
}